import { ApiResponseError } from '../../types/errors';

// Types of error along with any extra data
export type PriceListErrorContexts = {
  duplicate_slug: { slug: string };
  create_failed: undefined;
  update_failed: undefined;

  delete_used_by_group: undefined;
  delete_has_child: undefined;
  delete_failed: undefined;

  clear_failed: undefined;
};

export class PriceListError<T extends keyof PriceListErrorContexts> extends Error {
  public readonly id: T;
  public readonly data: PriceListErrorContexts[T];

  constructor(
    id: T,
    // Only require specifying extra data if it's not undefined
    ...args: PriceListErrorContexts[T] extends undefined
      ? [data?: undefined]
      : [data: PriceListErrorContexts[T]]
  ) {
    super(id);
    this.name = 'PriceListError';
    this.id = id;
    this.data = args[0] as PriceListErrorContexts[T];
    Object.setPrototypeOf(this, PriceListError.prototype);
  }
}

export class PriceListCsvError extends Error {
  public readonly errors: string[];

  constructor(errors: string[]) {
    super('CSV Validation Failed');
    this.name = 'PriceListCsvError';
    this.errors = errors;
    Object.setPrototypeOf(this, PriceListCsvError.prototype);
  }
}

type CsvValidationItem = { line?: number; field: string; message: string };
type NotFoundBody = { detail: string };

export type PriceListDeleteError = PriceListError<
  'delete_used_by_group' | 'delete_has_child' | 'delete_failed'
>;
export type PriceListCreateError = PriceListError<'create_failed' | 'duplicate_slug'>;
type PriceListUpdateError = PriceListError<'update_failed'>;

export type PriceListUpsertError = PriceListCreateError | PriceListUpdateError | PriceListCsvError;

export function csvErrorsFromRequestError(err: unknown): string[] {
  if (err instanceof ApiResponseError && err.status === 400) {
    return (err.data as CsvValidationItem[]).map((x) => {
      if (x.line && typeof x.line === 'number') {
        return `Line ${x.line} (${x.field}): ${x.message}`;
      }

      return `${x.message}`;
    });
  }

  if (err instanceof ApiResponseError && err.status === 404) {
    return [(err.data as NotFoundBody).detail];
  }

  return ['An issue occurred, please contact support.'];
}
